import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Card, Avatar, Row, Col } from 'antd';
import axiosInstance from '../../../axiosInstance';
import { BACKENDURLFiles, getUserData } from '../../constants';

const { Option } = Select;

const userData = getUserData();
const rolePermission = userData && userData?.user ? userData?.user?.role?.role_permission : {};

const Users = () => {
    const [employees, setEmployees] = useState([]);
    const [shops, setShops] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState([]);

    useEffect(() => {
        fetchEmployees();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchOnlineUsers();
        }, 15000);
        return () => clearInterval(interval);
    }, [shops]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/employees');
            const { employees, shops = [], roles = [] } = response.data;

            const currentUserIsSystemOwner = rolePermission?.is_system_owner === 1 || rolePermission?.is_system_owner === true;

            const filteredEmployees = employees.filter(employee => {
                const roleName = employee.user?.role?.name?.toLowerCase();
                return currentUserIsSystemOwner || roleName !== 'system owner';
            });

            const filteredRoles = roles.filter(role => {
                return currentUserIsSystemOwner || role.name.toLowerCase() !== 'system owner';
            });

            setEmployees(filteredEmployees);
            setShops(shops);
            setRoles(filteredRoles);

            if (shops.length > 0) {
                fetchOnlineUsers(shops[0].id);
            }
        } catch (error) {
            message.error('Failed to fetch employees.');
        } finally {
            setLoading(false);
        }
    };

    const fetchOnlineUsers = async (retailerParam = null) => {
        try {
            const retailer_id = retailerParam || localStorage.getItem('retailerId');
            if (!retailer_id) return;
            const response = await axiosInstance.get(`online-users/retailer/${retailer_id}`);
            const onlineUsersData = response.data.online_users || [];
            setOnlineUsers(onlineUsersData);
        } catch (error) {
            console.error('Failed to fetch online users:', error);
        }
    };

    const showModal = async (employee = null) => {
        setEditingEmployee(employee);
        if (employee) {
            try {
                let shopIds = [];
                
                // Fetch all shop assignments for this user
                try {
                    const response = await axiosInstance.get(`/user-shops/${employee.user.id}`);
                    shopIds = response.data.shops.map(shop => shop.id);
                } catch (error) {
                    console.error('Failed to fetch user shops:', error);
                    // Fallback to the single shop from the current employee data
                    shopIds = employee.shop_id ? [employee.shop_id] : [];
                }
                
                form.setFieldsValue({
                    name: employee.user.name,
                    email: employee.user.email,
                    phone: employee.user.phone,
                    role_id: employee.user.role_id,
                    shop_ids: shopIds,
                });
            } catch (error) {
                console.error('Error setting form values:', error);
                // Set basic values even if shop fetch fails
                form.setFieldsValue({
                    name: employee.user.name,
                    email: employee.user.email,
                    phone: employee.user.phone,
                    role_id: employee.user.role_id,
                    shop_ids: employee.shop_id ? [employee.shop_id] : [],
                });
            }
        } else {
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingEmployee) {
                await axiosInstance.put(`/employees/${editingEmployee.user.id}`, values);
                message.success('Employee updated successfully');
            } else {
                await axiosInstance.post('/register', values);
                message.success('Employee added successfully');
            }

            setIsModalVisible(false);
            form.resetFields();
            setEditingEmployee(null);
            fetchEmployees();
        } catch (error) {
            console.error('Error saving employee:', error);
            message.error('Failed to save employee.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingEmployee(null);
    };

    const isUserOnline = (userId) => {
        return onlineUsers.some(user => user.id === userId);
    };

    const changeUserStatus = async (userId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            await axiosInstance.post(`/change-user-status/${userId}`, { status: newStatus });
            message.success(`User status changed to ${newStatus}`);
            fetchEmployees();
        } catch (error) {
            console.error('Failed to change user status:', error);
            message.error('Failed to change user status.');
        }
    };

    const columns = [
        {
            title: 'Profile',
            key: 'profile',
            render: (_, record) => (
                <Avatar
                    src={record.user?.profile_photo_url || 'https://via.placeholder.com/40'}
                    size={40}
                    style={{ objectFit: 'cover', boxShadow: '0 0 5px rgba(0,0,0,0.2)' }}
                />
            ),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        { title: 'Name', dataIndex: ['user', 'name'], key: 'name', responsive: ['sm', 'md', 'lg', 'xl'] },
        { title: 'Email', dataIndex: ['user', 'email'], key: 'email', responsive: ['md', 'lg', 'xl'] },
        { title: 'Phone', dataIndex: ['user', 'phone'], key: 'phone', responsive: ['md', 'lg', 'xl'] },
        { title: 'Shop', dataIndex: ['shop', 'name'], key: 'shop', responsive: ['md', 'lg', 'xl'] },
        { title: 'Role', dataIndex: ['user', 'role', 'name'], key: 'role', responsive: ['md', 'lg', 'xl'] },
        {
            title: 'Online',
            key: 'onlineStatus',
            render: (_, record) => (
                isUserOnline(record.user?.id) ? (
                    <span className="glow-dot online">Online</span>
                ) : (
                    <span className="glow-dot offline">Offline</span>
                )
            ),
            responsive: ['sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <span style={{ textTransform: 'capitalize' }}>
                    {record.user?.status || 'inactive'}
                </span>
            ),
            responsive: ['sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Row gutter={[8, 8]} justify="start">
                    <Col>
                        <Button size="small" onClick={() => showModal(record)}>Edit</Button>
                    </Col>
                    <Col>
                        <Button
                            size="small"
                            type={record.user?.status === 'active' ? 'danger' : 'primary'}
                            onClick={() => changeUserStatus(record.user?.id, record.user?.status)}
                        >
                            {record.user?.status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                    </Col>
                </Row>
            ),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
    ];

    return (
        <div style={{ padding: '10px' }}>
            {/* Glow Dot CSS */}
            <style>
                {`
                .glow-dot {
                    display: inline-flex;
                    align-items: center;
                    font-weight: bold;
                    font-size: 14px;
                }
                .glow-dot::before {
                    content: "";
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    margin-right: 6px;
                }
                .glow-dot.online::before {
                    background: #52c41a;
                    box-shadow: 0 0 5px #52c41a, 0 0 10px #52c41a;
                    animation: pulseGreen 1.5s infinite;
                }
                .glow-dot.offline::before {
                    background: #ff4d4f;
                    box-shadow: 0 0 5px #ff4d4f;
                    animation: pulseRed 1.5s infinite;
                }
                @keyframes pulseGreen {
                    0% { box-shadow: 0 0 5px #52c41a; }
                    50% { box-shadow: 0 0 15px #52c41a; }
                    100% { box-shadow: 0 0 5px #52c41a; }
                }
                @keyframes pulseRed {
                    0% { box-shadow: 0 0 5px #ff4d4f; }
                    50% { box-shadow: 0 0 15px #ff4d4f; }
                    100% { box-shadow: 0 0 5px #ff4d4f; }
                }
                `}
            </style>

            {/* Online Users */}
            <Card title="🟢 Live Online Users" style={{ marginBottom: 20 }}>
                <Row gutter={[16, 16]}>
                    {onlineUsers.length > 0 ? onlineUsers.map((user) => (
                        <Col key={user.id} xs={12} sm={8} md={6} lg={4} xl={3}>
                            <div style={{ textAlign: 'center' }}>
                                <Avatar
                                    size={64}
                                    src={user.profile_pic_url || 'https://via.placeholder.com/64'}
                                    style={{ border: '2px solid #52c41a' }}
                                />
                                <div style={{ marginTop: 8, fontWeight: 'bold' }}>{user.name}</div>
                            </div>
                        </Col>
                    )) : (
                        <div style={{ width: '100%', textAlign: 'center' }}>No online users currently.</div>
                    )}
                </Row>
            </Card>

            {/* Add User Button */}
            <div style={{ textAlign: 'right', marginBottom: 16 }}>
                <Button type="primary" onClick={() => showModal()}>Add User</Button>
            </div>

            {/* Users Table */}
            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
                columns={columns}
                dataSource={employees}
                rowKey={(record) => record.user.id}
                loading={loading}
                scroll={{ x: 'max-content' }}
            />

            {/* Add/Edit Modal */}
            <Modal
                title={editingEmployee ? 'Edit User' : 'Add User'}
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                okText="Save"
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                        <Input placeholder="Enter name" />
                    </Form.Item>
                    <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                        <Input placeholder="Enter email" />
                    </Form.Item>
                    <Form.Item label="Phone" name="phone" rules={[{ required: true }]}>
                        <Input placeholder="Enter phone number" />
                    </Form.Item>
                    <Form.Item label="Role" name="role_id" rules={[{ required: true }]}>
                        <Select placeholder="Select role">
                            {roles.map(role => (
                                <Option key={role.id} value={role.id}>{role.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item 
                        label="Shops" 
                        name="shop_ids" 
                        rules={[{ required: true, message: 'Please select at least one shop' }]}
                    >
                        <Select mode="multiple" placeholder="Select shop(s)">
                            {shops.map(shop => (
                                <Option key={shop.id} value={shop.id}>{shop.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Users;